import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import {
  Button,
  Card,
  Chip,
  ConfirmDeleteModal,
  EmptyState,
  Input,
  Modal,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import { useDoors, useLocationTree } from '../api/hooks'
import { api } from '../api/client'
import { useAuthStore } from '../stores/auth'
import { readOnlyReason } from '../lib/dryrun'
import type { Door, DoorKind } from '../api/types'
import { locationBarriers, locationDescendants, locationPaths } from '../lib/locations'
import { LocationLink } from '../components/LocationLink'
import { SelectField } from './Directory'

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

export const DOOR_KIND_LABELS: Record<DoorKind, string> = {
  door: 'Door',
  mailbox: 'Mailbox',
  trash: 'Trash',
}

/** Client mirror of the backend's create-time kind auto-detect ('PB …'
 *  AND the techfass '15 - PB …' sequence-number shape → mailbox,
 *  sug/sopor/trash → trash). Used for the workbench's predicted kind
 *  chip and the create form's auto option — detection only, no calls.
 *  When the candidate carries `detail.door_kind` (techfass: informed by
 *  the mirrored reader MODULE type — an MTMBOX is a mailbox whatever
 *  the name says) THAT wins over this name guess. */
export function doorKindFromName(name: string): DoorKind {
  if (/^\s*(?:\d{1,3}\s*[-–.)]\s*)?pb[\s\-_.:]/i.test(name || '')) return 'mailbox'
  if (/\b(sug|sopor|trash)\b/i.test(name || '')) return 'trash'
  return 'door'
}

/** The predicted kind for a mapping candidate: the backend's
 *  model-informed detail.door_kind when present, else the name rule. */
export function candidateDoorKind(item: { display: string; detail?: Record<string, unknown> }): DoorKind {
  const k = item.detail?.door_kind
  if (k === 'door' || k === 'mailbox' || k === 'trash') return k
  return doorKindFromName(item.display)
}

/** The row-level kind: the backend's kind when present, else plain door. */
function doorKind(d: Door): DoorKind {
  return d.kind ?? 'door'
}

/** Same pill idiom as the Events page severity filter. */
function FilterChip({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-sm border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors focus:outline-none focus:ring-2 focus:ring-ostrel-gold ${
        active
          ? 'border-ostrel-navy bg-ostrel-navy text-ostrel-light'
          : 'border-ostrel-line bg-ostrel-card text-ostrel-ink-muted hover:bg-ostrel-sand'
      }`}
    >
      {label}
    </button>
  )
}

function DoorFormModal({
  open,
  door,
  onClose,
}: {
  open: boolean
  /** Null = create; a Door = edit (location optional on update). */
  door: Door | null
  onClose: () => void
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const tree = useLocationTree()

  const [name, setName] = useState('')
  const [location, setLocation] = useState('')
  /** Create: '' = auto-detect from the name (backend rule, mirrored by
   *  doorKindFromName). Edit: the door's current kind. */
  const [kind, setKind] = useState<DoorKind | ''>('')

  const save = useMutation({
    mutationFn: () =>
      door
        ? api.updateDoor(door.unique_id, {
            name: name.trim(),
            ...(location ? { location } : {}),
            ...(kind !== '' ? { kind } : {}),
          })
        : api.createDoor({ name: name.trim(), location, ...(kind !== '' ? { kind } : {}) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope, 'doors'] })
      onClose()
    },
  })

  useEffect(() => {
    if (open) {
      setName(door?.name ?? '')
      setLocation(door?.location ?? '')
      setKind(door ? doorKind(door) : '')
      save.reset()
    }
    // save.reset is stable; only reset when the modal (re)opens on a door.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, door])

  const paths = useMemo(() => (tree.data ? locationPaths(tree.data) : new Map<string, string>()), [tree.data])
  const barriers = useMemo(() => locationBarriers(tree.data ?? []), [tree.data])
  const locationOptions = useMemo(
    () =>
      [...paths.entries()].map(([uid, path]) => ({
        value: uid,
        label: barriers.has(uid) ? `${path} 🔒` : path,
      })),
    [paths, barriers],
  )

  return (
    <Modal open={open} title={door ? 'Edit door' : 'New door'} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() && location) save.mutate()
        }}
      >
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Main entrance"
          autoFocus
        />
        <SelectField
          label="Location"
          value={location}
          onChange={setLocation}
          options={[{ value: '', label: '— select location —' }, ...locationOptions]}
        />
        <SelectField
          label="Kind"
          value={kind}
          onChange={(v) => setKind(v as DoorKind | '')}
          options={
            door
              ? (Object.entries(DOOR_KIND_LABELS) as [DoorKind, string][]).map(([value, label]) => ({ value, label }))
              : [
                  { value: '', label: `Auto-detect (${DOOR_KIND_LABELS[doorKindFromName(name)]})` },
                  ...(Object.entries(DOOR_KIND_LABELS) as [DoorKind, string][]).map(([value, label]) => ({
                    value,
                    label,
                  })),
                ]
          }
        />
        <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
          A door is an opening at a location — readers and controllers are placed on it from the
          mapping workbench.
        </p>
        {save.isError && <p className="text-xs text-ostrel-danger">{errText(save.error)}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="navy" type="submit" disabled={save.isPending || !name.trim() || !location}>
            {save.isPending ? 'Saving…' : door ? 'Save changes' : 'Create door'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default function DoorsPage() {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const doors = useDoors()
  const tree = useLocationTree()

  const [searchParams, setSearchParams] = useSearchParams()
  const locationFilter = searchParams.get('location') ?? ''

  const [createOpen, setCreateOpen] = useState(false)
  const [editDoor, setEditDoor] = useState<Door | null>(null)
  const [confirmDoor, setConfirmDoor] = useState<Door | null>(null)
  const [kindFilter, setKindFilter] = useState<DoorKind | 'all'>('all')

  const paths = useMemo(() => (tree.data ? locationPaths(tree.data) : new Map<string, string>()), [tree.data])
  const descendants = useMemo(
    () => (tree.data && locationFilter ? locationDescendants(tree.data, locationFilter) : null),
    [tree.data, locationFilter],
  )

  const filtered = (doors.data ?? []).filter((d) => {
    if (kindFilter !== 'all' && doorKind(d) !== kindFilter) return false
    if (locationFilter && (!descendants || !descendants.has(d.location))) return false
    return true
  })

  const locationFilterLabel = locationFilter ? (paths.get(locationFilter) ?? locationFilter) : null

  const remove = useMutation({
    mutationFn: (uid: string) => api.deleteDoor(uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope, 'doors'] })
      setConfirmDoor(null)
    },
  })

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Estate"
        title="Doors"
        action={<Button onClick={() => setCreateOpen(true)}>+ New door</Button>}
      />

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
          Kind
        </span>
        <FilterChip active={kindFilter === 'all'} label="All" onClick={() => setKindFilter('all')} />
        <FilterChip active={kindFilter === 'door'} label="Doors" onClick={() => setKindFilter('door')} />
        <FilterChip
          active={kindFilter === 'mailbox'}
          label="Mailboxes"
          onClick={() => setKindFilter('mailbox')}
        />
        <FilterChip active={kindFilter === 'trash'} label="Trash" onClick={() => setKindFilter('trash')} />
        {locationFilterLabel && (
          <>
            <span className="mx-1 h-4 w-px bg-ostrel-line" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Location
            </span>
            <span
              className="inline-flex max-w-xs items-center gap-1.5 rounded-sm border border-ostrel-gold bg-ostrel-gold/10 px-2.5 py-1 text-[10px] font-semibold text-ostrel-gold"
              title={locationFilter}
            >
              <span className="truncate">{locationFilterLabel}</span>
              <button
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(searchParams)
                  next.delete('location')
                  setSearchParams(next, { replace: true })
                }}
                className="leading-none hover:text-ostrel-ink"
                aria-label="Clear location filter"
              >
                ×
              </button>
            </span>
          </>
        )}
      </div>

      <Card
        title="Doors"
        action={
          doors.data && (
            <span className="tnum text-[11px] text-ostrel-ink-muted">
              {doors.data.length} {doors.data.length === 1 ? 'door' : 'doors'}
            </span>
          )
        }
      >
        {doors.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : doors.isError ? (
          <EmptyState title="Could not load doors" hint={errText(doors.error)} />
        ) : doors.data.length === 0 ? (
          <EmptyState
            title="No doors yet"
            hint="Create a door at a location, or let the mapping workbench place synced door hardware."
            action={
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                + New door
              </Button>
            }
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No doors match"
            hint={
              locationFilter
                ? 'No doors at the selected location. Clear the location filter to see all doors.'
                : 'Try another kind filter.'
            }
          />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Door</TH>
                <TH>Location</TH>
                <TH className="text-right">Action</TH>
              </tr>
            </THead>
            <TBody>
              {filtered.map((d) => {
                // A door bound to a dry-run system is read-only: the edit
                // would never reach the portal.
                const hint = readOnlyReason(d)
                return (
                <TR key={d.unique_id}>
                  <TD>
                    <span className="flex items-center gap-2">
                      <span>
                        <span className="block text-sm font-medium text-ostrel-ink">{d.name}</span>
                        <span className="tnum block font-mono text-[10px] text-ostrel-ink-muted">
                          {d.unique_id}
                        </span>
                      </span>
                      {doorKind(d) !== 'door' && <Chip tone="info" label={DOOR_KIND_LABELS[doorKind(d)]} />}
                      {d.state && <Chip tone="muted" label={d.state} />}
                    </span>
                  </TD>
                  <TD>
                    <span className="text-xs text-ostrel-ink-muted">
                      <LocationLink uid={d.location}>{d.breadcrumb.join(' / ') || d.location}</LocationLink>
                    </span>
                  </TD>
                  <TD className="text-right">
                    <span className="flex items-center justify-end gap-2">
                      {hint && <span className="text-[10px] text-ostrel-ink-muted">{hint}</span>}
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={hint !== undefined}
                        title={hint}
                        onClick={() => setEditDoor(d)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={hint !== undefined}
                        title={hint}
                        onClick={() => setConfirmDoor(d)}
                      >
                        Delete
                      </Button>
                    </span>
                  </TD>
                </TR>
                )
              })}
            </TBody>
          </Table>
        )}
      </Card>

      <DoorFormModal open={createOpen} door={null} onClose={() => setCreateOpen(false)} />
      <DoorFormModal open={editDoor !== null} door={editDoor} onClose={() => setEditDoor(null)} />

      <ConfirmDeleteModal
        open={confirmDoor !== null}
        title="Delete door"
        objectName={confirmDoor?.name ?? ''}
        expected={confirmDoor?.unique_id ?? ''}
        pending={remove.isPending}
        error={remove.isError ? errText(remove.error) : null}
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete'}
        onConfirm={() => confirmDoor && remove.mutate(confirmDoor.unique_id)}
        onClose={() => setConfirmDoor(null)}
      >
        <p>
          Delete <span className="font-medium">{confirmDoor?.name}</span>
          {confirmDoor ? ` at ${confirmDoor.breadcrumb.join(' / ') || confirmDoor.location}` : ''}?
          Door policies and placements targeting it are removed with it.
        </p>
      </ConfirmDeleteModal>
    </div>
  )
}
